import React, { useMemo, useState } from 'react';
import _ from 'lodash';
import { useQuery } from '@apollo/client';
import makeStyles from '@mui/styles/makeStyles';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ModalsKeys, Nullable } from 'global';
import { useModal } from 'hooks/useModal';
import { DialogHeader } from 'components/shared/Dialog/DialogHeader';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';

import { dealActivityApi } from '../../api';
import { DealSmsActivity, VoContact, VoContactsQuery } from '../../api/types';
import { ActivityType, SmsConversationData } from '../../types';
import { generatePersonaFullName, getPersonaInitials } from 'utils/formatters';
import { MESSAGES_AVATAR_COLORS } from 'modules/dealActivity/constants';

export const NewMessageDialog = () => {
  const classes = useStyles();

  const { handleClose, isOpen, data } = useModal(ModalsKeys.NewMessageDialog);

  const [selectedContactId, setSelectedContactId] = useState<Nullable<string>>(null);
  const [selectedVoId, setSelectedVoId] = useState<Nullable<string>>(null);

  const voId = selectedVoId ?? data?.voId;
  const voOptions = data?.voOptions ?? [];

  const { data: response, loading } = useQuery<VoContactsQuery>(dealActivityApi.voContacts, {
    skip: !isOpen || !voId,
    variables: { VOId: voId },
  });

  const contacts = useMemo(() => {
    if (!response?.creditApplication) return [];

    const { creditApplicationOwner, creditApplicationContacts } = response.creditApplication;
    const unique = new Map<string, VoContact>();
    [...creditApplicationOwner, ...creditApplicationContacts].forEach(c => {
      if (c.ownerPgId && !unique.has(c.ownerPgId)) unique.set(c.ownerPgId, c);
    });

    return Array.from(unique.values());
  }, [response]);

  const handleCloseDialog = () => {
    setSelectedContactId(null);
    setSelectedVoId(null);
    handleClose();
  };

  const handleChangeVo = (nextVoId: string) => {
    setSelectedVoId(nextVoId);
    setSelectedContactId(null);
  };

  const handleConfirm = () => {
    const contact = contacts.find(c => c.ownerPgId === selectedContactId);

    if (!contact || !voId || !data?.onConfirm) return;

    // Sequence numbers are per VO, so only count this VO's conversations
    const smsActivities = data.activityList.filter(
      (a): a is DealSmsActivity => a.type === ActivityType.SMS && a.parentId === voId
    );
    const maxItem = _.maxBy(smsActivities, 'conversationSequenceNumber');
    const nextConversationSequenceNumber = maxItem ? maxItem.conversationSequenceNumber + 1 : 0;

    const conversationData: SmsConversationData = {
      voId,
      conversationSequenceNumber: nextConversationSequenceNumber,
      contactId: contact.ownerPgId,
      contactName: generatePersonaFullName(contact),
      contactPhone: contact.mobilePhone || contact.phone || contact.homePhone,
      contactTitle: contact.title ?? null,
      contactInitials: getPersonaInitials(contact.firstName, contact.lastName),
    };

    data.onConfirm(conversationData);
    handleCloseDialog();
  };

  return (
    <Dialog open={isOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
      <DialogHeader label="Start New Chat" handleClose={handleCloseDialog} />

      <Divider />

      <DialogContent>
        {voOptions.length > 1 && (
          <TextField
            select
            fullWidth
            size="small"
            label="Opportunity"
            value={voId ?? ''}
            sx={{ marginBottom: '1rem', marginTop: '0.5rem' }}
            onChange={event => handleChangeVo(event.target.value)}
          >
            {voOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        <RenderWithFallback
          isLoading={loading}
          fallBack={
            <Stack alignItems="center" justifyContent="center" sx={{ padding: '2rem 0' }}>
              <CircularProgress />
            </Stack>
          }
        >
          <Stack gap="1rem">
            <Typography variant="subtitle2" component="span">
              Select Recipent
            </Typography>

            <Stack>
              {contacts.map((contact, index) => {
                const fullName = generatePersonaFullName(contact);
                const initials = getPersonaInitials(contact.firstName, contact.lastName);
                const isSelected = selectedContactId === contact.ownerPgId;
                const avatarColor = MESSAGES_AVATAR_COLORS[index % MESSAGES_AVATAR_COLORS.length];
                const contactPhone = contact.mobilePhone || contact.phone || contact.homePhone;

                return (
                  <Box
                    key={contact.ownerPgId}
                    className={classes.contactRow}
                    onClick={!!contactPhone ? () => setSelectedContactId(contact.ownerPgId) : undefined}
                    sx={{
                      backgroundColor: isSelected ? '#EEF7FE' : 'transparent',
                      cursor: !!contactPhone ? 'pointer' : 'default',
                      pointerEvents: !!contactPhone ? 'auto' : 'none',
                    }}
                  >
                    <Avatar
                      sx={{ width: 32, height: 32, backgroundColor: avatarColor, color: 'black', fontSize: '12px' }}
                    >
                      <Typography fontSize="0.75rem" fontWeight={500}>
                        {initials}
                      </Typography>
                    </Avatar>

                    <Stack flex={1} gap="1px" justifyContent="center">
                      <Typography variant="subtitle2" component="span">
                        {fullName}
                        {' • '}
                        {!!contactPhone ? (
                          `${contactPhone}`
                        ) : (
                          <Typography component="span" variant="body2" fontSize="12px" sx={{ color: '#ffa21a' }}>
                            {'(no phone)'}
                          </Typography>
                        )}
                      </Typography>

                      {contact.title && (
                        <Typography variant="caption" color="textSecondary">
                          {contact.title}
                        </Typography>
                      )}
                    </Stack>

                    {contact.pc && (
                      <Box className={classes.primaryChip}>
                        <Typography fontSize="12px" fontWeight={500} color="#616161">
                          Primary
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Stack>
        </RenderWithFallback>
      </DialogContent>

      <Divider />

      <Stack direction="row" justifyContent="flex-end" gap="0.5rem" sx={{ padding: '1rem 1.5rem' }}>
        <Button variant="outlined" color="primary" onClick={handleCloseDialog}>
          Cancel
        </Button>

        <Button variant="contained" color="primary" disabled={!selectedContactId} onClick={handleConfirm}>
          Confirm & create
        </Button>
      </Stack>
    </Dialog>
  );
};

const useStyles = makeStyles({
  contactRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 8px',
    borderBottom: '1px solid #EEEEEE',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  primaryChip: {
    padding: '2px 8px',
    borderRadius: '4px',
    backgroundColor: '#EEEEEE',
  },
});
